import { useState, useEffect } from 'react';
import { isIOS, isIOSStandalone, isAndroid } from '../utils/deviceDetection';
import { PLAY_STORE_URL, APP_STORE_URL } from '../constants/pwa';
import { analytics } from '../services/analytics';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const STORAGE_KEYS = {
  DISMISSED_TIMESTAMP: 'pwa-install-dismissed',
  PERMANENTLY_DISMISSED: 'pwa-install-never-show'
};

const DISMISS_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

export const usePWAInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    const checkIfInstalled = () => {
      // Check if running in standalone mode (PWA is installed)
      if (window.matchMedia('(display-mode: standalone)').matches) {
        setIsInstalled(true);
        return true;
      }
      
      // Check for iOS Safari standalone mode
      if (isIOSStandalone()) {
        setIsInstalled(true);
        return true;
      }
      
      return false;
    };

    // Check if user has permanently dismissed or is within dismiss period
    const checkDismissalStatus = () => {
      try {
        // Check if permanently dismissed
        const permanentlyDismissed = localStorage.getItem(STORAGE_KEYS.PERMANENTLY_DISMISSED);
        if (permanentlyDismissed === 'true') {
          return true;
        }

        // Check if within dismiss period
        const dismissedTimestamp = localStorage.getItem(STORAGE_KEYS.DISMISSED_TIMESTAMP);
        if (dismissedTimestamp) {
          const dismissedTime = parseInt(dismissedTimestamp, 10);
          const currentTime = Date.now();
          
          if (currentTime - dismissedTime < DISMISS_DURATION) {
            return true; // Still within dismiss period
          } else {
            // Dismiss period expired, clear the timestamp
            localStorage.removeItem(STORAGE_KEYS.DISMISSED_TIMESTAMP);
          }
        }
      } catch (error) {
        console.warn('Error checking PWA dismissal status:', error);
      }
      
      return false;
    };

    const installed = checkIfInstalled();
    const dismissed = checkDismissalStatus();
    const iosDevice = isIOS();

    if (installed) {
      return;
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('beforeinstallprompt fired');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      
      // Only show prompt if not dismissed and not installed
      // But always save the deferred prompt for manual installation
      if (!dismissed && !installed) {
        setShowInstallPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      console.log('PWA was installed');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
      
      // Clear any dismissal records since app is now installed
      try {
        localStorage.removeItem(STORAGE_KEYS.DISMISSED_TIMESTAMP);
        localStorage.removeItem(STORAGE_KEYS.PERMANENTLY_DISMISSED);
      } catch (error) {
        console.warn('Error clearing PWA dismissal records:', error);
      }
    };

    // For iOS devices, we redirect to App Store, but still listen for beforeinstallprompt for consistency
    // For other devices, we use the standard beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // For iOS devices without beforeinstallprompt support, show prompt based on dismissal status
    if (iosDevice && !dismissed && !installed) {
      setShowInstallPrompt(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    // For Android devices, redirect to Google Play Store
    if (isAndroid()) {
      try {
        analytics.logAppStoreRedirect('android');
      } catch (error) {
        console.warn('Failed to track app store redirect:', error);
      }
      window.open(PLAY_STORE_URL, '_blank', 'noopener,noreferrer');
      return true; // Indicate action was taken
    }

    // For iOS devices, redirect to Apple App Store
    if (isIOS()) {
      try {
        analytics.logAppStoreRedirect('ios');
      } catch (error) {
        console.warn('Failed to track app store redirect:', error);
      }
      window.open(APP_STORE_URL, '_blank', 'noopener,noreferrer');
      return true; // Indicate action was taken
    }

    if (!deferredPrompt) {
      // For other devices when no deferred prompt is available
      return false;
    }

    try {
      // Show the install prompt
      await deferredPrompt.prompt();

      // Wait for the user's response
      const { outcome } = await deferredPrompt.userChoice;

      console.log(`User response: ${outcome}`);

      // Clear the deferredPrompt and hide any visible prompts
      setDeferredPrompt(null);
      setShowInstallPrompt(false);

      if (outcome === 'dismissed') {
        // User declined via manual install, record dismissal with timestamp
        // This only affects the automatic prompt, not the header button
        try {
          localStorage.setItem(STORAGE_KEYS.DISMISSED_TIMESTAMP, Date.now().toString());
        } catch (error) {
          console.warn('Error saving PWA dismissal timestamp:', error);
        }
      }

      return outcome === 'accepted';
    } catch (error) {
      console.error('Error during install prompt:', error);
      return false;
    }
  };

  const dismissPrompt = (permanent = false) => {
    setShowInstallPrompt(false);
    
    try {
      if (permanent) {
        // User chose "Don't ask again" for automatic prompts
        localStorage.setItem(STORAGE_KEYS.PERMANENTLY_DISMISSED, 'true');
        localStorage.removeItem(STORAGE_KEYS.DISMISSED_TIMESTAMP);
      } else {
        // User chose "Maybe later" - dismiss automatic prompt for 30 days
        localStorage.setItem(STORAGE_KEYS.DISMISSED_TIMESTAMP, Date.now().toString());
      }
    } catch (error) {
      console.warn('Error saving PWA dismissal preference:', error);
    }
    
    // Keep the deferredPrompt available for manual installation via header button
    // Don't clear it even for permanent dismissal
  };

  const resetDismissal = () => {
    try {
      localStorage.removeItem(STORAGE_KEYS.DISMISSED_TIMESTAMP);
      localStorage.removeItem(STORAGE_KEYS.PERMANENTLY_DISMISSED);
    } catch (error) {
      console.warn('Error resetting PWA dismissal:', error);
    }
  };

  return {
    // For automatic prompt display
    showInstallPrompt: showInstallPrompt && !isInstalled,
    // For manual installation (header button, etc.)
    canInstall: (!!deferredPrompt || isIOS() || isAndroid()) && !isInstalled,
    isInstalled,
    handleInstall,
    dismissPrompt,
    resetDismissal // Useful for testing or admin purposes
  };
};